/**
 * What may be done while the device is away from the server (PROJECT_SPEC.md §8.5, OFF-010, OFF-011).
 *
 * The rule is an allow-list, deliberately: a new kind of command is online-only until someone decides it is
 * safe to capture without the server, instead of offline-capable until someone remembers to forbid it.
 *
 * Signatures and approvals are never taken offline (§8.5 option 1 — "performed offline, signed on
 * reconnect"). 21 CFR 11.200 needs two identification components and 11.50 a manifestation the server
 * itself produces; a device cannot honestly supply either, and the pre-issued-token alternative (§8.5
 * option 2) is not built, so the attempt is blocked rather than half-honoured.
 */
import type { OfflineAnchor } from "./interval.js";

export type OfflineVerdict =
  | { ok: true }
  | {
      ok: false;
      code: "OFFLINE_SIGNATURE_REFUSED" | "OFFLINE_COMMAND_NOT_PERMITTED";
      message: string;
      cite: string;
    };

/** Capture commands safe to queue: the server re-validates every one of them on replay (N6, N7). */
const OFFLINE_COMMANDS = new Set(["RecordValue", "StepAction"]);
/** Step actions that are plain capture; holds, voids, skips and aborts change what the record means. */
const OFFLINE_STEP_EVENTS = new Set(["Start", "Complete"]);
/** Anything that signs, approves, certifies or releases. */
const SIGNATURE_CLASS = /sign|approv|certif|releas|dispositi|review|revers|verif|check/i;

export function offlineVerdict(commandType: string, payload: Record<string, unknown> = {}): OfflineVerdict {
  if (SIGNATURE_CLASS.test(commandType))
    return {
      ok: false,
      code: "OFFLINE_SIGNATURE_REFUSED",
      message: `${commandType} is a signature or approval and cannot be taken offline. Perform the work now and sign it once the device is back online.`,
      cite: "PROJECT_SPEC.md §8.5; 21 CFR 11.200(a)",
    };
  if (!OFFLINE_COMMANDS.has(commandType))
    return {
      ok: false,
      code: "OFFLINE_COMMAND_NOT_PERMITTED",
      message: `${commandType} can only be done while connected.`,
      cite: "PROJECT_SPEC.md §8.2",
    };
  if (commandType === "StepAction" && !OFFLINE_STEP_EVENTS.has(String(payload["event"])))
    return {
      ok: false,
      code: "OFFLINE_COMMAND_NOT_PERMITTED",
      message: `A step cannot be ${String(payload["event"]).toLowerCase()} offline; only starting and completing are captured.`,
      cite: "PROJECT_SPEC.md §8.2",
    };
  return { ok: true };
}

/** Monotonic ns → elapsed ms since the anchor; a reading before the anchor counts as zero. */
export function elapsedSinceAnchorMs(anchor: OfflineAnchor, monotonicNowNs: string): number {
  const d = BigInt(monotonicNowNs) - BigInt(anchor.monotonicAtSync);
  return Number((d > 0n ? d : 0n) / 1_000_000n);
}

export type CaptureGate =
  | { open: true; remainingMs: number }
  | { open: false; reason: "NO_ANCHOR" | "WINDOW_EXCEEDED"; message: string };

/**
 * OFF-011: the capture gate. Measured on the monotonic clock, which the user cannot wind back, so changing
 * the tablet's date does not buy more offline time. Past the limit the device must sync before it captures.
 */
export function captureGate(
  anchor: OfflineAnchor | null,
  monotonicNowNs: string,
  maxOfflineMs: number,
): CaptureGate {
  if (!anchor)
    return {
      open: false,
      reason: "NO_ANCHOR",
      message: "This device has no trusted time from the server. Connect and sync before capturing offline.",
    };
  const elapsed = elapsedSinceAnchorMs(anchor, monotonicNowNs);
  if (elapsed > maxOfflineMs)
    return {
      open: false,
      reason: "WINDOW_EXCEEDED",
      message: "The offline limit has been reached. Connect and sync before recording anything further.",
    };
  return { open: true, remainingMs: maxOfflineMs - elapsed };
}
